export type TrackingStatus = {
  status_name?: string;
  city_name?: string;
  status_date_time?: string;
};

export type TrackingPackage = {
  number?: string;
  weight?: string | number;
  length?: string | number;
  width?: string | number;
  height?: string | number;
  comment?: string;
  items_count?: number;
};

export type TrackingRecord = {
  delivery_block?: { state?: "active" | "released" | "unknown"; amount: string | null } | null;
  access_expires_at?: string;
  tenant_name?: string;
  sender_page_url?: string;
  cdek_number?: string;
  moysklad_order_number?: string;
  recipient_name?: string;
  city_name?: string;
  country_code?: string;
  pvz_code?: string;
  pvz_address?: string;
  current_status?: string;
  current_status_city?: string;
  current_status_at?: string;
  packages?: TrackingPackage[];
  statuses?: TrackingStatus[];
};

type TrackingPayload = {
  access_expires_at?: string;
  tracking?: TrackingRecord;
  detail?: string;
};

const TOKEN_LENGTH = 5;

export class TrackingAccessRequired extends Error {}

export async function verifyTrackingAccess(token: string, digits: string): Promise<void> {
  const response = await fetch(`/api/track/${encodeURIComponent(token)}/verify`, {
    method: "POST", credentials: "same-origin", cache: "no-store",
    headers: { "Content-Type": "application/json", "X-Tracking-Verification": "1" },
    body: JSON.stringify({ digits }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = new Error(typeof payload.detail === "string" ? payload.detail : "Не удалось проверить доступ. Попробуйте позже.");
    Object.assign(error, { retryAfter: Number(response.headers.get("Retry-After")) || 0 });
    throw error;
  }
}

export function trackingToken(pathname = window.location.pathname): string {
  return pathname.split("/").filter(Boolean).pop() || "";
}

export function validTrackingToken(token: string): boolean {
  return new RegExp(`^[a-z0-9]{${TOKEN_LENGTH}}$`, "i").test(token);
}

export async function loadTracking(token: string, signal?: AbortSignal): Promise<TrackingRecord> {
  const response = await fetch(`/api/track/${encodeURIComponent(token)}`, {
    credentials: "same-origin", cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  const payload = (await response.json().catch(() => ({}))) as TrackingPayload;
  if (response.status === 401) throw new TrackingAccessRequired();
  if (!response.ok || !payload.tracking) {
    throw new Error(payload.detail || "Отправление не найдено");
  }
  return { ...payload.tracking, access_expires_at: payload.access_expires_at };
}

export function recordSenderPageClick(token: string): void {
  const url = `/api/track/${encodeURIComponent(token)}/events`;
  const body = JSON.stringify({ event_type: "sender_page_click" });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    // Analytics is best-effort and must never block navigation.
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body,
    keepalive: true,
  }).catch(() => undefined);
}
